import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, Sparkles, Zap, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PREMIUM_MAINTENANCE } from "@/lib/featureFlags";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ price_eur: 9.99, yearly_price_eur: 107.89, yearly_savings_pct: 10, free_collection_limit: 50, free_history_days: 30, referral_threshold: 3, referral_discount_pct: 20 });
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data.premium));
    if (user && user.email) {
      refresh();
      api.get("/subscription/status").then((r) => setSubStatus(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubscribe = async () => {
    if (PREMIUM_MAINTENANCE) {
      toast.info(t("pricing.maintenance_toast"));
      return;
    }
    if (!user || !user.email) {
      nav("/login?redirect=/pricing");
      return;
    }
    if (user.is_premium) {
      toast.success(t("pricing.already_premium") + " 👑");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin, plan });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user && user.is_premium;
  const hasReferralDiscount = subStatus?.referral_discount_active || user?.referral_discount_active;

  const monthlyDisplay = subStatus?.monthly_price_eur ?? meta.price_eur;
  const yearlyDisplay = subStatus?.yearly_price_eur ?? meta.yearly_price_eur;
  const currentPrice = plan === "monthly" ? monthlyDisplay : yearlyDisplay;
  const currentBase = plan === "monthly" ? meta.price_eur : meta.yearly_price_eur;

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="font-mono text-xs text-[#E11D48] tracking-widest mb-2">{t("pricing.label")}</div>
        <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter leading-none text-slate-900">
          {t("pricing.title_1")} <span className="text-[#FACC15]">{t("pricing.title_2")}</span>
        </h1>
        <p className="text-slate-600 mt-5 leading-relaxed">{t("pricing.subtitle")}</p>

        {PREMIUM_MAINTENANCE && (
          <div
            data-testid="premium-maintenance-banner"
            className="mt-8 inline-flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-left max-w-xl"
          >
            <Wrench className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-bold text-amber-900 text-sm uppercase tracking-wider mb-1">
                {t("pricing.maintenance_title")}
              </div>
              <p className="text-amber-800 text-sm leading-relaxed">
                {t("pricing.maintenance_desc")}
              </p>
            </div>
          </div>
        )}

        {!PREMIUM_MAINTENANCE && (
          /* Plan toggle */
          <div className="inline-flex items-center mt-8 rounded-full border border-slate-200 bg-white p-0.5 shadow-sm" data-testid="plan-toggle">
            <button
              onClick={() => setPlan("monthly")}
              data-testid="plan-monthly-btn"
              className={`px-5 py-2 rounded-full text-xs font-heading font-semibold uppercase tracking-wider transition ${plan === "monthly" ? "bg-[#E11D48] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              {t("pricing.monthly")}
            </button>
            <button
              onClick={() => setPlan("yearly")}
              data-testid="plan-yearly-btn"
              className={`px-5 py-2 rounded-full text-xs font-heading font-semibold uppercase tracking-wider transition inline-flex items-center gap-1.5 ${plan === "yearly" ? "bg-[#E11D48] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              {t("pricing.yearly")}
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${plan === "yearly" ? "bg-white text-[#E11D48]" : "bg-[#FACC15]/20 text-amber-700"}`}>
                {t("pricing.save_pct", { n: meta.yearly_savings_pct })}
              </span>
            </button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* FREE */}
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="pricing-free-card">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">
            <Zap className="w-3.5 h-3.5" /> {t("pricing.free")}
          </div>
          <div className="font-heading font-black text-4xl tracking-tighter mb-2 text-slate-900">
            {t("pricing.free_price")}
            <span className="text-base text-slate-400 font-body font-normal">{t("pricing.forever")}</span>
          </div>
          <p className="text-slate-500 text-sm mb-8">{t("pricing.free_desc")}</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> {t("pricing.free_feats.browse")}</li>
            <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> {t("pricing.free_feats.submit")}</li>
            <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> {t("pricing.free_feats.avg")}</li>
            <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> {t("pricing.free_feats.collection", { n: meta.free_collection_limit })}</li>
            <li className="flex items-start gap-2 text-slate-700"><Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> {t("pricing.free_feats.history", { n: meta.free_history_days })}</li>
          </ul>
        </div>

        {/* PREMIUM */}
        <div
          className="rounded-2xl border-2 border-[#FACC15] bg-white p-8 shadow-md relative"
          data-testid="pricing-premium-card"
          style={{ background: "linear-gradient(180deg, #FFF9DB 0%, #FFFFFF 60%)" }}
        >
          <div className="absolute -top-3 right-6 bg-[#E11D48] text-white px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest font-bold shadow-sm">
            {t("pricing.recommended")}
          </div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber-700 mb-3">
            <Crown className="w-3.5 h-3.5" /> {t("pricing.premium")}
          </div>
          <div className="font-heading font-black text-4xl tracking-tighter mb-1 text-slate-900">
            €{Number(currentPrice).toFixed(2)}
            <span className="text-base text-slate-400 font-body font-normal">
              {plan === "monthly" ? t("pricing.per_month") : t("pricing.per_year")}
            </span>
          </div>
          {hasReferralDiscount && currentPrice < currentBase && (
            <div className="font-mono text-xs text-[#E11D48] tracking-wider mb-3 flex items-center gap-2">
              <span className="line-through text-slate-400">€{Number(currentBase).toFixed(2)}</span>
              <span className="px-2 py-0.5 rounded-full bg-[#E11D48]/10 border border-[#E11D48]/30 text-[#E11D48]">
                -{meta.referral_discount_pct}% {t("pricing.discounted")}
              </span>
            </div>
          )}
          {plan === "yearly" && (
            <div className="font-mono text-xs text-slate-500 mb-4 tracking-wider">
              ≈ €{(currentPrice / 12).toFixed(2)}{t("pricing.per_month")}
            </div>
          )}
          <p className="text-slate-600 text-sm mb-8 mt-2">{t("pricing.premium_desc")}</p>
          <ul className="space-y-3 text-sm mb-8">
            {[
              { title: t("pricing.feats.alerts_title"), desc: t("pricing.feats.alerts_desc") },
              { title: t("pricing.feats.collection_title"), desc: t("pricing.feats.collection_desc", { n: meta.free_collection_limit }) },
              { title: t("pricing.feats.history_title"), desc: t("pricing.feats.history_desc", { n: meta.free_history_days }) },
              { title: t("pricing.feats.csv_title"), desc: t("pricing.feats.csv_desc") },
              { title: t("pricing.feats.badge_title"), desc: t("pricing.feats.badge_desc") },
              { title: t("pricing.feats.adfree_title"), desc: t("pricing.feats.adfree_desc") },
            ].map((f) => (
              <li key={f.title} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#E11D48] mt-0.5 flex-shrink-0" />
                <span>
                  <span className="font-semibold text-slate-900">{f.title}</span>
                  <span className="text-slate-600"> — {f.desc}</span>
                </span>
              </li>
            ))}
          </ul>

          <button
            onClick={onSubscribe}
            disabled={loading || isPremium || PREMIUM_MAINTENANCE}
            data-testid="pricing-subscribe-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#E11D48] text-white font-heading font-bold uppercase tracking-wider text-sm shadow-md hover:bg-[#BE123C] disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {PREMIUM_MAINTENANCE ? (
              <><Wrench className="w-4 h-4" /> {t("pricing.maintenance_btn")}</>
            ) : isPremium ? (
              <><Crown className="w-4 h-4" /> {t("pricing.already_premium")}</>
            ) : loading ? (
              t("pricing.redirecting")
            ) : (
              <><Sparkles className="w-4 h-4" /> {plan === "monthly" ? t("pricing.subscribe_monthly") : t("pricing.subscribe_yearly")}</>
            )}
          </button>

          <p className="text-xs text-slate-400 text-center mt-3 font-mono">
            {PREMIUM_MAINTENANCE ? t("pricing.maintenance_secured") : t("pricing.secured")}
          </p>
        </div>
      </div>

      <p className="text-center text-slate-400 text-xs mt-10 font-mono">{t("pricing.test_mode")}</p>
    </div>
  );
}
